import { Node, Slice } from '@tiptap/pm/model'
import { EditorView } from '@tiptap/pm/view'
import { find } from 'linkifyjs'
import { nanoid } from 'nanoid'

import type {
  Embed,
  EmbedInput,
  Network,
} from '@tribeplatform/gql-client/types'
import { useCreateImages } from '@tribeplatform/react-sdk/hooks'

import { fileSizeLimitError, linkToFile } from '../../common/lib/upload.js'
import { I18nShape } from '../../i18n/types.js'
import { UploadQueueContextProps } from '../../Providers/UploadQueueProvider.js'
import {
  AsyncImageAttributes,
  ImageDescriptionType,
} from '../extensions/image/types.js'
import { composerError } from '../utils/errors.js'
import { reconcileImage } from '../utils/image.js'

interface HandlePastedFilesProps {
  view: EditorView
  files: File[]
  createImages: ReturnType<typeof useCreateImages>['mutateAsync']
  network: Network
  intl: I18nShape
  addToUploadQueue: UploadQueueContextProps['addToUploadQueue']
  removeFromUploadQueue: UploadQueueContextProps['removeFromUploadQueue']
}

export const handlePastedFiles = ({
  view,
  files,
  createImages,
  network,
  intl,
  addToUploadQueue,
  removeFromUploadQueue,
}: HandlePastedFilesProps) => {
  const asyncImageNodeType = view.state.schema.nodes['async-image']
  files.forEach(async file => {
    const tempId = nanoid()
    const uploadQueueItem = { id: tempId }
    addToUploadQueue(uploadQueueItem)

    try {
      const node = asyncImageNodeType.create({
        src: URL.createObjectURL(file),
        id: tempId,
        loading: true,
        uploadId: tempId,
      })

      if (file.size > network?.settings?.imageSizeLimit) {
        composerError(intl)(
          fileSizeLimitError(network?.settings?.imageSizeLimit, 'image', intl),
        )
        return
      }

      const transaction = view.state.tr.replaceSelectionWith(node)
      view.dispatch(transaction)

      const [
        {
          urls: { full: url },
          id,
        },
      ] = await createImages([{ file, size: file.size }])

      await reconcileImage(tempId, view, asyncImageNodeType, {
        id,
        src: url,
      })
    } finally {
      removeFromUploadQueue(uploadQueueItem)
    }
  })
  return true
}

export const handlePastedContent = (
  view: EditorView,
  slice: Slice,
  createImages: ReturnType<typeof useCreateImages>['mutateAsync'],
  network: Network,
  intl: I18nShape,
  addToUploadQueue: UploadQueueContextProps['addToUploadQueue'],
  removeFromUploadQueue: UploadQueueContextProps['removeFromUploadQueue'],
  embed: (variables: EmbedInput) => Promise<Embed>,
) => {
  slice.content.forEach(node => {
    if (node.type.name === 'async-image') {
      handlePastedImage(
        node,
        view,
        createImages,
        network,
        intl,
        addToUploadQueue,
        removeFromUploadQueue,
      )
    }

    // Only handle embeds when one link is pasted and nothing is selected
    if (
      node.type.name === 'paragraph' &&
      slice.content.childCount === 1 &&
      view.state.selection.empty
    ) {
      const parsedUrl = getParsedUrlFromNode(node)
      if (parsedUrl) {
        handleLinkToEmbed(view, parsedUrl, embed)
      }
    }
  })
}

const getParsedUrlFromNode = (node: Node) => {
  const text = node.textContent
  const url = text.trim()
  const matched = find(url)
    .filter(link => link.isLink)
    .map(link => ({
      text: link.value,
      index: link.start,
      data: link,
    }))
  if (matched.length > 0) {
    const [firstMatch] = matched
    const parsedUrl = firstMatch.data.href
    return parsedUrl
  }

  return null
}

const handleLinkToEmbed = async (
  view: EditorView,
  parsedUrl: string,
  embed: (variables: EmbedInput) => Promise<Embed>,
) => {
  try {
    const embedResult = await embed({ url: parsedUrl })
    if (embedResult?.html) {
      if (view.dispatch) {
        const embedNodeType = view.state.schema.nodes.embed
        const node = embedNodeType.create({
          embed: embedResult,
        })
        view.dispatch(view.state.tr.replaceSelectionWith(node))
      }
    }
  } catch (error) {
    // No embeds were found, do nothing
  }
}

const handlePastedImage = async (
  node: Node,
  view: EditorView,
  createImages: ReturnType<typeof useCreateImages>['mutateAsync'],
  network: Network,
  intl: I18nShape,
  addToUploadQueue: UploadQueueContextProps['addToUploadQueue'],
  removeFromUploadQueue: UploadQueueContextProps['removeFromUploadQueue'],
) => {
  const asyncImageNodeType = view.state.schema.nodes['async-image']

  // Mutate current node and assign a targetable ID to it
  const tempId = nanoid()
  const uploadQueueItem = { id: tempId }
  addToUploadQueue(uploadQueueItem)

  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    node.attrs.id = tempId

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    node.attrs.loading = true

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    node.attrs.uploadId = tempId

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    node.attrs.description = node.attrs.description || node.attrs.alt

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    node.attrs.hasDescription = !!node.attrs.description

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    node.attrs.descriptionType = !node.attrs.alt
      ? ImageDescriptionType.Caption
      : ImageDescriptionType.Alt

    // Fetch the src into a file
    const { src } = node.attrs
    const file = await linkToFile(src, intl)
    // Throw an error if the file size is exceed the limit
    if (!file) {
      // TODO: Update the node to display errouneous state
      return
    }

    if (file.size > network?.settings?.imageSizeLimit) {
      composerError(intl)(
        fileSizeLimitError(network?.settings?.imageSizeLimit, 'image', intl),
      )
      return
    }

    // Upload the file
    const [
      {
        urls: { full: url },
        id,
      },
    ] = await createImages([{ file, size: file.size }])

    const newNodeAttrs: Required<AsyncImageAttributes> = {
      id,
      src: url,
      loading: false,
      alt: node.attrs.alt,
      title: node.attrs.title,
      align: node.attrs.align,
      size: node.attrs.size,
      app: node.attrs.app,
      description: node.attrs.description,
      descriptionType: node.attrs.descriptionType,
      hasDescription: node.attrs.hasDescription || false,
      uploadId: undefined,
    }

    await reconcileImage(tempId, view, asyncImageNodeType, {
      ...newNodeAttrs,
      id,
      src: url,
    })
  } finally {
    removeFromUploadQueue(uploadQueueItem)
  }
}
